import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { MetaRatingIcon } from "@/icons";
import { playerRoleLabelSelector } from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { MetaRatingLogoSvg } from "../../../svgs/MetaRatingLogoSvg";

type Props = {
  reward: SBCRewardType;
  setName?: string;
  setDescription?: string;
  onlyShowImage?: boolean;
  onClick?: () => void;
};

export const SetRewardHeader = ({
  reward,
  setName,
  setDescription,
  onlyShowImage = false,
  onClick,
}: Props) => {
  const {
    type,
    metaRating,
    untradeable,
    name,
    description: rewardDescription,
  } = reward;

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const rewardImage =
    reward.type === "playerpick"
      ? `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fut23/misc/playerpick.png`
      : reward.rewardImgURL || reward.packImageURL;

  const rewardTitle = type === "player" ? setName : name;
  const description =
    type === "player" || type === "playerpick" || type === "cosmetic-evo"
      ? setDescription
      : rewardDescription;

  const playerRole = useSelector(
    playerRoleLabelSelector({
      playerRoleId: metaRating?.playerRoleId,
    }),
  );

  return (
    <div className="flex justify-center items-center">
      <div className="flex justify-center items-center min-w-28 min-h-28 p-0 m-0">
        <img
          src={rewardImage}
          alt={rewardTitle || "sbc-logo"}
          className={clsx(
            "max-h-36 touch-device:max-h-[150px] transition-all duration-300",
            reward.type === "pack" && "p-3",
            reward.type === "player" &&
              "max-mobile:scale-110 max-mobile:-mt-2 cursor-pointer mobile:hover:scale-[102%]",
          )}
          onClick={onClick}
        />
      </div>

      {!onlyShowImage && (
        <div className={clsx("flex flex-row my-auto")}>
          <div
            className={clsx(
              "flex flex-col text-white",
              isMobile && "p-0 m-0 gap-y-1",
            )}
          >
            <div
              className={clsx(
                "flex",
                rewardTitle?.length > 15 ? "flex-col" : "flex-row gap-x-1",
                isMobile && "gap-x-0",
              )}
            >
              <p className="text-base text-dark-secondary">{rewardTitle}</p>
              {type !== "player" && (
                <p className="my-auto text-xs text-white/50">
                  {type === "cosmetic-evo"
                    ? ""
                    : untradeable
                      ? "[UNTRADEABLE]"
                      : "[TRADEABLE]"}
                </p>
              )}
            </div>

            {playerRole && (
              <div className="flex flex-col gap-y-1">
                <span className="text-sm text-dark-secondary">
                  {playerRole}
                </span>
                <div className="flex flex-row gap-x-2 items-center text-dark-secondary">
                  <MetaRatingIcon width="20" height="20" />
                  <span className="text-base font-semibold text-dark-primary">
                    {metaRating?.metarating?.toFixed(1)}
                  </span>
                </div>
              </div>
            )}

            <p
              className={clsx(
                "text-xs text-white/50 large:max-w-[300px] mt-1",
                isMobile && "max-w-full",
              )}
            >
              {description}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
